/**
 * The onboarding page's clears on leaving a twin (`spec-onboarding-persistent-scene-editor-pose`,
 * R5.4; `spec-onboarding-persistent-scene-handover`, R3.6): the handed-back view and any held picture
 * never outlive the page's visit to one twin.
 *
 * They run on unmount and on a change of twin, at once, as they always have, unless the live
 * viewer's Edit has a picture held or a camera handed on for that twin and no plan map has taken it
 * yet (`spec-onboarding-persistent-scene-edit-flight`, R6). Then the clear waits a microtask, and a
 * mount for the same twin calls it off: React's development rehearsal (`<StrictMode>`, `index.tsx`)
 * unmounts and mounts every effect once on the first mount, and run at once the clears would drop
 * the Edit's hand-on and cut its picture on every local run. A real unmount, or a change to another
 * twin, still clears before any map can mount.
 */
import { clearHandback, liveHandOnWaiting } from '../../components/3D/planView/planPoseHandback';
import { heldEditFor, releaseHold } from '../../components/3D/planView/planHandover';

interface Pending {
  twinBid: string | undefined;
  cancelled: boolean;
}

let pending: Pending | null = null;

const clearNow = () => {
  clearHandback();
  releaseHold({ immediate: true });
};

/** The page left `twinBid` (unmounted, or moved to another twin): clear, a microtask from now. */
export function schedulePageClear(twinBid: string | undefined): void {
  if (!heldEditFor(twinBid) && !liveHandOnWaiting(twinBid)) {
    clearNow();
    return;
  }
  const mine: Pending = { twinBid, cancelled: false };
  pending = mine;
  const run = () => {
    if (pending === mine) pending = null;
    if (!mine.cancelled) clearNow();
  };
  if (typeof queueMicrotask === 'function') queueMicrotask(run);
  else void Promise.resolve().then(run);
}

/** The page mounted for `twinBid`: a clear still waiting for that same twin is called off. */
export function cancelPageClear(twinBid: string | undefined): void {
  if (pending && pending.twinBid === twinBid) {
    pending.cancelled = true;
    pending = null;
  }
}
